import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { cn } from '@worldcoo/ui/lib/utils';
import { LanguageSelect } from './language-select';
import { Logo } from './logo';
import { useTranslation } from '@/lib/i18n';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@worldcoo/ui/components/drawer';
import { MenuIcon, XIcon } from 'lucide-react';
import { Separator } from '@worldcoo/ui/components/separator';
import { ClientOnly, Link, isNotFound } from '@tanstack/react-router';
import { usePortalContext } from '@/lib/use-portal-context';
import { Suspense } from 'react';
import { useIsInframe } from '@/lib/use-is-in-iframe';
import { usePortalMetadataQuery } from '@/lib/queries';

export function Header() {
	return (
		<Suspense>
			<SuspensedHeader />
		</Suspense>
	);
}

function SuspensedHeader() {
	const { portalId } = usePortalContext();
	const { i18n } = useTranslation();
	const isInIframe = useIsInframe();
	const portalMetadata = usePortalMetadataQuery();

	if (isInIframe) return;
	if (isNotFound(portalMetadata)) return null;

	return (
		<div className="bg-background sticky top-0 z-20 border-b">
			<header className="mx-auto flex h-20 max-w-[1366px] items-center justify-between gap-3 px-6">
				{/* Limit logo here for cases where the logo itself has a weird aspect-ratio like comerciaglobalpayments */}
				<Link
					to="/$portalId"
					params={{ portalId }}
					onClick={() => window.scrollTo(0, 0)}
					className="max-w-[300px] flex-1"
				>
					<Logo />
				</Link>

				<div className="hidden items-center gap-3 py-3 sm:flex">
					{!portalMetadata.hideAppLink && (
						<a
							href="https://app.worldcoo.com"
							target="_blank"
							rel="noopener noreferrer"
							className={cn(buttonVariants({ variant: 'ghost', size: '2xl' }), 'font-normal')}
						>
							{i18n.nav.app}
						</a>
					)}

					<LanguageSelect />
				</div>

				{/* Not needed for SEO, unload these from server to improve performance */}
				<ClientOnly>
					<Drawer direction="right">
						<DrawerTrigger className="ml-auto py-6 sm:hidden">
							<MenuIcon className="size-8" />
						</DrawerTrigger>
						<DrawerContent aria-describedby={undefined}>
							<DrawerHeader className="flex-row items-center justify-between border-b">
								<DrawerTitle className="text-start text-2xl">{i18n.nav.title}</DrawerTitle>
								<DrawerClose asChild>
									<Button type="button" variant="ghost" size="icon">
										<XIcon className="size-7" strokeWidth={1} />
										<span className="sr-only">{i18n.actions.close}</span>
									</Button>
								</DrawerClose>
							</DrawerHeader>

							{!portalMetadata.hideAppLink && (
								<a
									href="https://app.worldcoo.com"
									target="_blank"
									rel="noopener noreferrer"
									className={cn(buttonVariants({ variant: 'ghost', size: '2xl' }), 'justify-end font-normal')}
								>
									{i18n.nav.app}
								</a>
							)}

							<div className="mx-3">
								<Separator />
							</div>

							<LanguageSelect className="ml-auto" />

							<div className="mx-3">
								<Separator />
							</div>
						</DrawerContent>
					</Drawer>
				</ClientOnly>
			</header>
		</div>
	);
}
